import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, CheckCircle2, Receipt } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export default function PortalBills() {
  const [data, setData] = useState({ bills: [], total_outstanding: 0, reminder_days: 15 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/portal/bills`, { withCredentials: true })
      .then(r => setData(r.data || { bills: [] }))
      .finally(() => setLoading(false));
  }, []);

  const overdue = data.bills.filter(b => b.is_overdue);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-zinc-50">Bills & Payments</h1>
        <p className="text-zinc-500 text-sm mt-1">Reminder window: {data.reminder_days} days from invoice date.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center gap-2 text-zinc-500 text-xs"><Receipt size={12} /> Total Outstanding</div>
          <div className="text-amber-300 font-mono font-bold text-2xl mt-2">{fmt(data.total_outstanding)}</div>
        </div>
        <div className={`border rounded-xl p-4 ${overdue.length ? 'bg-red-900/20 border-red-800/40' : 'bg-green-900/10 border-green-800/30'}`}>
          {overdue.length ? (
            <><div className="flex items-center gap-2 text-red-300 text-xs"><AlertTriangle size={12} /> Overdue Bills</div>
              <div className="text-red-400 font-mono font-bold text-2xl mt-2">{overdue.length}</div></>
          ) : (
            <><div className="flex items-center gap-2 text-green-300 text-xs"><CheckCircle2 size={12} /> Status</div>
              <div className="text-green-300 font-semibold text-lg mt-2">All caught up</div></>
          )}
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm" data-testid="bills-table">
          <thead>
            <tr className="border-b border-zinc-800">
              {['Invoice', 'Date', 'Age', 'Total', 'Paid', 'Outstanding', 'Status'].map(h => (
                <th key={h} className="text-left text-zinc-500 text-xs font-medium px-4 py-2.5">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-6 text-center text-zinc-500 text-sm">Loading…</td></tr>}
            {!loading && data.bills.length === 0 && (
              <tr><td colSpan={7} className="p-10 text-center">
                <CheckCircle2 size={24} className="text-green-500 mx-auto mb-2 opacity-70" />
                <div className="text-zinc-400 text-sm">No outstanding bills</div>
              </td></tr>
            )}
            {data.bills.map(b => (
              <tr key={b.id} className={`border-b border-zinc-800/50 hover:bg-zinc-800/30 ${b.is_overdue ? 'bg-red-900/[0.08]' : ''}`} data-testid={`bill-${b.id}`}>
                <td className="px-4 py-3 font-mono text-amber-400 text-xs font-semibold">{b.invoice_number || b.id.slice(0, 10)}</td>
                <td className="px-4 py-3 text-zinc-400 text-xs font-mono">{b.date}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${b.is_overdue ? 'bg-red-900/40 text-red-300' : 'bg-zinc-800 text-zinc-400'}`}>
                    {b.age_days} day{b.age_days !== 1 ? 's' : ''}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-zinc-300 text-xs">{fmt(b.total)}</td>
                <td className="px-4 py-3 font-mono text-green-400 text-xs">{fmt(b.paid)}</td>
                <td className="px-4 py-3 font-mono text-red-300 text-xs font-semibold">{fmt(b.outstanding)}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${b.status === 'Partial' ? 'bg-amber-900/40 text-amber-300' : 'bg-red-900/30 text-red-400'}`}>
                    {b.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
